import React, { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import {
  DEFAULT_WIDGET_OPTIONS,
  getAspectRatio,
  getBannerContainerStyles,
  getBannerImages,
  getHeight,
  getTopMarginByAnnouncement,
  useUpgradeEditorBannerSelection,
  useWindowResize,
  WidgetProps,
} from './utils';
import { useWidgetDndContextData } from '@/context/WidgetDndContext';
import { Chevrons } from './components/Chevrons';
import { useGetDeviceType } from 'src/hooks/useGetDeviceType';
import { ROOT_PARENT } from 'src/components/WidgetMaker/WidgetDnD/CreateHTML';
import { ADMIN_WIDGETS_NAMES, OLD_WIDGET_NAMES } from 'src/constants/widgets';
import { useSelector } from 'src/redux';
import { RootState } from 'src/redux/reducers';
import useCarouselVideoConfig from './hook/CarouselVideoHook';
import IntermittentBanner from './components/IntermittentBanner';
import { useGetCarouselRef } from './hook/useGetCarouselRef';
import { BannerItem } from './components/BannerItem';

const Slider = dynamic(
  () => import('./components/Slider').then((mod) => mod.ReactSlickCarousel),
  {
    ssr: false,
    loading: IntermittentBanner,
  }
);

export const CarouselWidget = (props: WidgetProps) => {
  const { sliderRefCallback, sliderRef, containerRef, containerRefCallback } =
    useGetCarouselRef();
  const [loadSlider, setLoadSlider] = useState(false);
  const {
    // hasAdminAccess,
    widgetContextState: {
      activeHeaderData,
      widgets,
      showPreview,
      globalStyle,
      isBuilder,
      previewDevice,
    },
  } = useWidgetDndContextData();

  const headerId = useSelector(
    (state: RootState) => state.storeReducer.store.theme?.components?.Header?.[0]?.id
  );
  const isBuilderChevronEnabled = globalStyle?.chevrons;

  const activeWidget = widgets[(props as any).id];
  const activeWidgetProps = activeWidget?.props;

  const widgetOptions = {
    ...DEFAULT_WIDGET_OPTIONS,
    ...props.widgetOptions,
    ...(activeWidgetProps || {}),
  };

  const { videoConfig, handleVideoDuration } = useCarouselVideoConfig(
    sliderRef,
    widgetOptions
  );

  const globalClass = activeHeaderData?.headerPropsConfig?.globalClass || {};

  const isTransparentHeader =
    globalClass?.staticClass?.base_config?.defaultFillColorType === 'transparent';
  const firstWidgetId = widgets[ROOT_PARENT]?.children?.[0];
  const firstWidget = widgets[firstWidgetId];
  const isBannerFirstWidget =
    firstWidget?.name === ADMIN_WIDGETS_NAMES.HERO_BANNER &&
    (props as any).id === firstWidgetId;

  const shouldShiftTextContainer = isTransparentHeader && isBannerFirstWidget;

  const isNewCarousel = props.forNewCarousel || widgetOptions.forNewCarousel;
  const isHeroBannerWidget = [
    OLD_WIDGET_NAMES.CAROUSEL,
    ADMIN_WIDGETS_NAMES.HERO_BANNER,
  ].includes(props.type);

  // const [, setScreenWidth] = useState(getInnerWidth());
  const { isMobile: isMobileFromServer } = useGetDeviceType();
  const isMobile = isBuilder ? previewDevice === 'mobile' : isMobileFromServer;

  const [activeSlide2, setActiveSlide2] = useState(0);
  const [height, setHeight] = useState(getHeight(widgetOptions, '100vw', isNewCarousel));
  const [, setAspectRatio] = useState(widgetOptions?.aspectRatio);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [videoElementRefs, setVideoElementRefs] = useState<HTMLVideoElement[]>([]);

  const isDesktop = !isMobile;
  const useImageOriginalHeight =
    props.widgetOptions?.[!isMobile ? 'height' : 'heightMobile'] === 'original';

  const adjustedMarginByAnnouncement = getTopMarginByAnnouncement({
    activeHeaderData,
    isMobile,
    isBannerFirstWidget,
    isTransparentHeader,
  });

  const bannerContainerStyles = getBannerContainerStyles({
    isNewCarousel,
    widgetOptions,
    containerRef,
    useImageOriginalHeight,
    height,
    isMobile,
    adjustedMarginByAnnouncement,
  });

  useWindowResize({
    dependencies: [showPreview],
    executor: adjustDimensions,
    widgetOptions,
    props,
  });

  const { handleNextClick, handlePrevClick, allSliderSettings } =
    useUpgradeEditorBannerSelection({
      activeSlide2,
      setActiveSlide2,
      id: (props as any).id,
      sliderRef,
      widgetOptions,
      isBuilderChevronEnabled,
    });

  useEffect(() => {
    if (headerId) {
      const headerElement = document.getElementById(headerId.toString());
      headerElement && setHeaderHeight(headerElement.clientHeight);
    }
  }, [activeHeaderData, headerId]);

  const images = getBannerImages({ props, activeWidgetProps, isDesktop });

  function adjustDimensions() {
    // setScreenWidth(getInnerWidth());
    setHeight(getHeight(widgetOptions, containerRef.current?.clientWidth, isNewCarousel));
    setAspectRatio(getAspectRatio(widgetOptions, containerRef.current?.clientWidth));
  }

  function getAdjustedShiftFromTop() {
    const bannerContainerBottomPadding = 24;
    return (headerHeight - bannerContainerBottomPadding) / 2;
  }

  useEffect(() => {
    if (
      loadSlider &&
      videoElementRefs[0] &&
      activeSlide2 === 0 &&
      allSliderSettings.autoplaySpeed
    ) {
      if (allSliderSettings.autoplaySpeed > 4000) {
        const timeoutID = setTimeout(() => {
          handleNextClick();
        }, allSliderSettings.autoplaySpeed - 4000);

        return () => {
          clearTimeout(timeoutID);
        };
      } else handleNextClick();
    }
  }, [loadSlider, videoElementRefs[0]]);

  if (!isBuilder && !loadSlider && isHeroBannerWidget) {
    return (
      <IntermittentBanner
        containerRefCallback={containerRefCallback}
        containerRef={containerRef}
        widgetOptions={widgetOptions}
        setLoadSlider={setLoadSlider}
      />
    );
  }

  if (!images?.length) return null;
  return (
    <div
      ref={containerRefCallback}
      className="carousel-widget-slider slider-container tw-relative"
      id="carousel-widget-slider"
    >
      {isBuilderChevronEnabled && (
        <Chevrons
          widgetOptions={widgetOptions}
          isDesktop={isDesktop}
          currentActiveSlideIndex={activeSlide2}
          handlePrevClick={handlePrevClick}
          handleNextClick={handleNextClick}
          images={images}
          videoConfig={videoConfig}
        />
      )}
      <div style={bannerContainerStyles}>
        {!!images.length && (
          <Slider
            {...allSliderSettings}
            ref={sliderRefCallback}
            sliderRefCallback={sliderRefCallback}
          >
            {images.map((image, index) => {
              const isCurrentSlideActive = activeSlide2 === index;
              const imageWrapperComponentStyles = {
                height: bannerContainerStyles.height,
                width: bannerContainerStyles.width,
              };
              return (
                <BannerItem
                  key={index}
                  image={image}
                  isCurrentSlideActive={isCurrentSlideActive}
                  index={index}
                  isNewCarousel={isNewCarousel}
                  widgetOptions={widgetOptions}
                  imageWrapperComponentStyles={imageWrapperComponentStyles}
                  bannerContainerStyles={bannerContainerStyles}
                  isMobile={isMobile}
                  videoElementRefs={videoElementRefs}
                  setVideoElementRefs={setVideoElementRefs}
                  handleVideoDuration={handleVideoDuration}
                  globalStyle={globalStyle}
                  shouldShiftTextContainer={shouldShiftTextContainer}
                  getAdjustedShiftFromTop={getAdjustedShiftFromTop}
                />
              );
            })}
          </Slider>
        )}
      </div>
    </div>
  );
};

export default CarouselWidget;
